import React, {
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  useCallback,
} from 'react';
import {
  getShipmentsGroupedByCountry,
  getShipmentsGroupedByCountryMonthYearETD,
  getShipmentsGroupedByCountryMonthYearETA,
  getShipmentsGroupedByPortCountryMonthYearETA,
  getShipmentsGroupedByPortCountryMonthYearETD,
  getVoyageSummary,
  getQuartelyVoyageNumbersETA,
  getQuartelyVoyageNumbersETD,
  getVoyageMonthlyTableETA,
  getVoyageMonthlyTableETD,
  getVoyageStatistics,
} from '../../services/VoyageRepository';
import {
  useBarChartProcessor,
  usePortBarChartProcessor,
  useTableDataProcessor,
} from '../../hooks/useVesselDataProcessing';
import { useReportingFilters } from './ReportingFiltersContext';
import type { ApiRecord, GridColumn, GridTableData } from './types';

export interface MapWidgetEntry {
  effectiveShippedCount: number;
  totalEntries: number;
}

export type MapWidgetData = Record<string, MapWidgetEntry>;

export interface ReportingChartDataContextValue {
  mapWidgetData: MapWidgetData | null;
  barChartWidgetDataETD: ApiRecord[];
  barChartWidgetDataETA: ApiRecord[];
  barChartPortWidgetDataETA: ApiRecord[];
  barChartPortWidgetDataETD: ApiRecord[];
  summaryData: ApiRecord;
  tableQuarterlyData: GridTableData;
  tableMonthlyData: GridTableData;
  tableStatisticsData: GridTableData;
  fetchChartsTabData: () => Promise<void>;
}

interface StatisticsAccumulator {
  totalDuration: number;
  countDuration: number;
  totalDaysBetweenEntries: number;
  countDaysBetweenEntries: number;
  totalDaysInitialToActualETD: number;
  countDaysInitialToActualETD: number;
  totalDaysInitialToActualETA: number;
  countDaysInitialToActualETA: number;
}

const ReportingChartDataContext =
  createContext<ReportingChartDataContextValue | null>(null);

export const ReportingChartDataProvider = ({ children }: PropsWithChildren) => {
  const { buildParams, showError } = useReportingFilters();

  const [mapWidgetData, setMapWidgetData] = useState<MapWidgetData | null>(
    null
  );
  const [barChartWidgetDataETD, setBarChartWidgetDataETD] = useState<
    ApiRecord[]
  >([]);
  const [barChartWidgetDataETA, setBarChartWidgetDataETA] = useState<
    ApiRecord[]
  >([]);
  const [barChartPortWidgetDataETA, setBarChartPortWidgetDataETA] = useState<
    ApiRecord[]
  >([]);
  const [barChartPortWidgetDataETD, setBarChartPortWidgetDataETD] = useState<
    ApiRecord[]
  >([]);
  const [tableQuarterlyData, setTableQuarterlyData] = useState<GridTableData>({
    rows: [],
    columns: [],
  });
  const [tableMonthlyData, setTableMonthlyData] = useState<GridTableData>({
    rows: [],
    columns: [],
  });
  const [summaryData, setSummaryData] = useState<ApiRecord>({});
  const [tableStatisticsData, setTableStatisticsData] = useState<GridTableData>(
    {
      rows: [],
      columns: [],
    }
  );

  const processBarChartResponse = useBarChartProcessor();
  const processPortBarChartResponse = usePortBarChartProcessor();
  const processTableData = useTableDataProcessor();

  const fetchVesselStatistics = useCallback(async (): Promise<ApiRecord[]> => {
    try {
      const params = buildParams();
      const response = await getVoyageStatistics(params);
      return response || [];
    } catch (error) {
      showError(error, 'fetchVesselStatistics');
      return [];
    }
  }, [buildParams, showError]);

  const fetchMapData = useCallback(async () => {
    try {
      const params = buildParams();
      const data: ApiRecord[] | null =
        await getShipmentsGroupedByCountry(params);
      const reducedData = data?.reduce<MapWidgetData>(
        (acc, item) => ({
          ...acc,
          [item.countryCode]: {
            effectiveShippedCount: item.sumEffectiveShipped,
            totalEntries: item.uniqueEntries,
          },
        }),
        {}
      );
      setMapWidgetData(reducedData || null);
    } catch (error) {
      showError(error, 'fetchMapData');
      setMapWidgetData(null);
    }
  }, [buildParams, showError]);

  const fetchBarChartData = useCallback(async () => {
    try {
      const params = buildParams();
      const etdParams = new URLSearchParams(params);
      etdParams.set('groupBy', 'departure');
      const etaParams = new URLSearchParams(params);
      etaParams.set('groupBy', 'destination');
      const [responseETD, responseETA] = await Promise.all([
        getShipmentsGroupedByCountryMonthYearETD(etdParams),
        getShipmentsGroupedByCountryMonthYearETA(etaParams),
      ]);
      const [etdData, etaData] = await Promise.all([
        processBarChartResponse(responseETD),
        processBarChartResponse(responseETA),
      ]);
      setBarChartWidgetDataETD(etdData || []);
      setBarChartWidgetDataETA(etaData || []);
    } catch (error) {
      showError(error, 'fetchBarChartData');
      setBarChartWidgetDataETD([]);
      setBarChartWidgetDataETA([]);
    }
  }, [buildParams, processBarChartResponse, showError]);

  const fetchPortBarChartData = useCallback(async () => {
    try {
      const params = buildParams();
      const etdParams = new URLSearchParams(params);
      etdParams.set('groupBy', 'departure');
      const etaParams = new URLSearchParams(params);
      etaParams.set('groupBy', 'destination');
      const [responseETA, responseETD] = await Promise.all([
        getShipmentsGroupedByPortCountryMonthYearETA(etaParams),
        getShipmentsGroupedByPortCountryMonthYearETD(etdParams),
      ]);
      const [etaData, etdData] = await Promise.all([
        processPortBarChartResponse(responseETA),
        processPortBarChartResponse(responseETD),
      ]);
      setBarChartPortWidgetDataETA(etaData || []);
      setBarChartPortWidgetDataETD(etdData || []);
    } catch (error) {
      showError(error, 'fetchPortBarChartData');
      setBarChartPortWidgetDataETA([]);
      setBarChartPortWidgetDataETD([]);
    }
  }, [buildParams, processPortBarChartResponse, showError]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const params = buildParams();
      const [summaryResponse, statisticsResponse] = await Promise.all([
        getVoyageSummary(params),
        fetchVesselStatistics(),
      ]);

      const stats = statisticsResponse.reduce<StatisticsAccumulator>(
        (acc, entry) => {
          if (entry.averageDuration !== null) {
            acc.totalDuration += entry.averageDuration;
            acc.countDuration++;
          }
          if (entry.averageDaysBetweenEntries !== null) {
            acc.totalDaysBetweenEntries += entry.averageDaysBetweenEntries;
            acc.countDaysBetweenEntries++;
          }
          if (entry.averageDaysInitialToActualETD !== null) {
            acc.totalDaysInitialToActualETD +=
              entry.averageDaysInitialToActualETD;
            acc.countDaysInitialToActualETD++;
          }
          if (entry.averageDaysInitialToActualETA !== null) {
            acc.totalDaysInitialToActualETA +=
              entry.averageDaysInitialToActualETA;
            acc.countDaysInitialToActualETA++;
          }
          return acc;
        },
        {
          totalDuration: 0,
          countDuration: 0,
          totalDaysBetweenEntries: 0,
          countDaysBetweenEntries: 0,
          totalDaysInitialToActualETD: 0,
          countDaysInitialToActualETD: 0,
          totalDaysInitialToActualETA: 0,
          countDaysInitialToActualETA: 0,
        }
      );

      const updatedSummaryData: ApiRecord = {
        ...summaryResponse,
        averageOfAllDurations: stats.countDuration
          ? parseFloat((stats.totalDuration / stats.countDuration).toFixed(2))
          : 0,
        averageBetweenAllEntries: stats.countDaysBetweenEntries
          ? parseFloat(
              (
                stats.totalDaysBetweenEntries / stats.countDaysBetweenEntries
              ).toFixed(2)
            )
          : 0,
        averageAllInitialToActualETD: stats.countDaysInitialToActualETD
          ? parseFloat(
              (
                stats.totalDaysInitialToActualETD /
                stats.countDaysInitialToActualETD
              ).toFixed(2)
            )
          : 0,
        averageAllInitialToActualETA: stats.countDaysInitialToActualETA
          ? parseFloat(
              (
                stats.totalDaysInitialToActualETA /
                stats.countDaysInitialToActualETA
              ).toFixed(2)
            )
          : 0,
      };
      setSummaryData(updatedSummaryData || {});
    } catch (error) {
      showError(error, 'fetchSummaryData');
      setSummaryData({});
    }
  }, [buildParams, fetchVesselStatistics, showError]);

  const fetchQuarterlyData = useCallback(async () => {
    try {
      const params = buildParams();
      const [eta, etd] = await Promise.all([
        getQuartelyVoyageNumbersETA(params),
        getQuartelyVoyageNumbersETD(params),
      ]);
      processTableData(
        { ETA: eta, ETD: etd },
        (item: ApiRecord) => `${item.quarter} ${item.year}`,
        setTableQuarterlyData
      );
    } catch (error) {
      showError(error, 'fetchQuarterlyData');
      setTableQuarterlyData({ rows: [], columns: [] });
    }
  }, [buildParams, processTableData, showError]);

  const fetchMonthlyData = useCallback(async () => {
    try {
      const params = buildParams();
      const [eta, etd] = await Promise.all([
        getVoyageMonthlyTableETA(params),
        getVoyageMonthlyTableETD(params),
      ]);
      processTableData(
        { ETA: eta, ETD: etd },
        (item: ApiRecord) =>
          new Date(item.monthYear).toLocaleString('default', {
            month: 'short',
            year: 'numeric',
          }),
        setTableMonthlyData
      );
    } catch (error) {
      showError(error, 'fetchMonthlyData');
      setTableMonthlyData({ rows: [], columns: [] });
    }
  }, [buildParams, processTableData, showError]);

  const fetchStatisticsData = useCallback(async () => {
    try {
      const response = await fetchVesselStatistics();
      const columns: GridColumn[] = [
        { field: 'supplierNo', headerName: 'Supplier No', width: 150 },
        { field: 'suppliername', headerName: 'Supplier Name', width: 250 },
        { field: 'totalEntries', headerName: 'Total Voyages', width: 150 },
        {
          field: 'effectiveShipped',
          headerName: 'Effective Shipped',
          width: 150,
        },
        {
          field: 'averageDaysBetweenEntries',
          headerName: 'Avg Frequency',
          width: 200,
        },
        {
          field: 'averageDuration',
          headerName: 'Avg Travel Time',
          width: 150,
        },
        {
          field: 'averageDaysInitialToActualETD',
          headerName: 'Avg Delay ETD',
          width: 250,
        },
        {
          field: 'averageDaysInitialToActualETA',
          headerName: 'Avg Delay ETA',
          width: 250,
        },
        {
          field: 'percentageEffectiveShipped',
          headerName: 'Percentage Effective Shipped',
          width: 250,
        },
      ];
      const rows = response.map((item) => ({
        id: item.supplierNo,
        supplierNo: item.supplierNo,
        suppliername: item.suppliername,
        totalEntries: item.totalEntries,
        effectiveShipped: item.effectiveShipped,
        averageDaysBetweenEntries: item.averageDaysBetweenEntries
          ? item.averageDaysBetweenEntries.toFixed(2)
          : null,
        averageDuration: item.averageDuration
          ? item.averageDuration.toFixed(2)
          : null,
        averageDaysInitialToActualETD: item.averageDaysInitialToActualETD
          ? item.averageDaysInitialToActualETD.toFixed(2)
          : null,
        averageDaysInitialToActualETA: item.averageDaysInitialToActualETA
          ? item.averageDaysInitialToActualETA.toFixed(2)
          : null,
        percentageEffectiveShipped: item.percentageEffectiveShipped
          ? item.percentageEffectiveShipped.toFixed(2)
          : null,
      }));
      setTableStatisticsData({ rows, columns });
    } catch (error) {
      showError(error, 'fetchStatisticsData');
      setTableStatisticsData({ rows: [], columns: [] });
    }
  }, [fetchVesselStatistics, showError]);

  const fetchChartsTabData = useCallback(async () => {
    await Promise.all([
      fetchMapData(),
      fetchBarChartData(),
      fetchPortBarChartData(),
      fetchSummaryData(),
      fetchQuarterlyData(),
      fetchMonthlyData(),
      fetchStatisticsData(),
    ]);
  }, [
    fetchMapData,
    fetchBarChartData,
    fetchPortBarChartData,
    fetchSummaryData,
    fetchQuarterlyData,
    fetchMonthlyData,
    fetchStatisticsData,
  ]);

  return (
    <ReportingChartDataContext.Provider
      value={{
        mapWidgetData,
        barChartWidgetDataETD,
        barChartWidgetDataETA,
        barChartPortWidgetDataETA,
        barChartPortWidgetDataETD,
        summaryData,
        tableQuarterlyData,
        tableMonthlyData,
        tableStatisticsData,
        fetchChartsTabData,
      }}
    >
      {children}
    </ReportingChartDataContext.Provider>
  );
};

export const useReportingChartData = (): ReportingChartDataContextValue => {
  const ctx = useContext(ReportingChartDataContext);
  if (ctx === null) {
    throw new Error(
      'useReportingChartData must be used within a <ReportingChartDataProvider>'
    );
  }
  return ctx;
};
